/**
 * Closed deal page (2026-11-01): what to show, and when to show nothing.
 *
 * Only 3.5% of edtech and library deals carry value, predicted expiry and incumbent sentiment
 * together, so every block on the page has three states — published, NEO estimate, not
 * available — and a block with no data is removed, never rendered as a row of dashes.
 *
 * 🔴 closed_deals.notes and predicted_expiry_source_note are pipeline logs ("VERIFIED … as
 * t0_contract_register", "Basis NOT RECORDED … v7 §5.3.7"). They were printed verbatim on the old
 * page. Nothing here reads them; the "how we dated this" line is derived from the term and the
 * confidence grade instead.
 */

/**
 * Source links that do not open the award itself. Measured on 42,314 non-English edtech/library
 * deals: 31% share their link with 5+ other deals because it points at a dataset, a portal or a
 * bulk download. A link like that looks broken to the user, so the page names the register and
 * the reference instead of linking.
 */
const NON_SPECIFIC_LINK_PATTERNS = [
  /dati\.anticorruzione\.it\/opendata/i,             // Italy ANAC: the CIG dataset, not the CIG
  /github\.com\/OpenAPC/i,                           // OpenAPC: one CSV for 1,618 deals
  /consortium\.ch\/type-produkt/i,                   // Swiss consortium product page
  /analisi\.transparenciacatalunya\.cat\/d\//i,      // Catalonia open-data portal
  /datosabiertos\.compraspublicas\.gob\.ec/i,        // Ecuador open data
  /bi\.seace\.gob\.pe/i,                             // Peru SEACE open data
  /comprasmx\.buengobierno\.gob\.mx\/datos-abiertos/i,
  /comprasestatales\.gub\.uy\/?$/i,
  /blob\.core\.windows\.net\/.*\.zip/i,              // Chile bulk zips
  /archiv\.simap\.ch\//i,                            // every archive link tested returns 404
  /data\.economie\.gouv\.fr\/api\//i,                // France DECP: raw API JSON
  /\.(zip|csv|xlsx?)(\?|$)/i,
];

const BARE_PORTAL_PATHS = new Set(['', '/', '/app/contratos', '/Base4/pt', '/datos-abiertos']);

export function isSpecificNoticeUrl(url) {
  const raw = String(url ?? '').trim();
  if (!/^https?:\/\//i.test(raw)) return false;
  if (NON_SPECIFIC_LINK_PATTERNS.some((re) => re.test(raw))) return false;
  try {
    const u = new URL(raw);
    if (BARE_PORTAL_PATHS.has(u.pathname.replace(/\/+$/, '')) && !u.search && !u.hash) return false;
  } catch {
    return false;
  }
  return true;
}

const REGISTER_LABELS = {
  pt_base: 'BASE, Portuguese public contracts register',
  es_placsp: 'Plataforma de Contratación del Sector Público (Spain)',
  es_cat_rpc: 'Registre públic de contractes de Catalunya',
  eu_ted: 'Tenders Electronic Daily (EU)',
  it_anac_cig: 'ANAC, Italian national procurement database',
  it_ubuy_portaleappalti: 'University procurement portal (U-Buy)',
  cz_registr_smluv: 'Registr smluv (Czech contract register)',
  sk_crz: 'Centrálny register zmlúv (Slovakia)',
  pl_cru: 'Centralny Rejestr Umów (Poland)',
  pl_ezamowienia: 'e-Zamówienia (Poland)',
  co_secop_i: 'SECOP I (Colombia)',
  co_secop_ii: 'SECOP II (Colombia)',
  cl_mercadopublico: 'Mercado Público (Chile)',
  cl_mercadopublico_oc: 'Mercado Público purchase orders (Chile)',
  br_pncp: 'Portal Nacional de Contratações Públicas (Brazil)',
  mx_compranet_archive: 'CompraNet (Mexico)',
  mx_comprasmx_datos_abiertos: 'ComprasMX (Mexico)',
  pe_seace_conosce: 'SEACE (Peru)',
  ec_sercop_ocds: 'SERCOP (Ecuador)',
  fr_decp: 'DECP, French public procurement data',
  fr_boamp: 'BOAMP (France)',
  nl_tenderned: 'TenderNed (Netherlands)',
  tenderned_nl: 'TenderNed (Netherlands)',
  ch_simap: 'SIMAP (Switzerland)',
  be_eprocurement: 'e-Procurement (Belgium)',
  at_bvergg_kerndaten: 'Austrian procurement notices (BVergG)',
  de_bkms: 'Öffentliche Vergabe (Germany)',
  openapc_transformative_agreements: 'OpenAPC transformative agreements',
};

/** A readable name for where the deal was published. */
export function registerLabel(deal) {
  const key = String(deal?.source_name ?? '').trim();
  if (REGISTER_LABELS[key]) return REGISTER_LABELS[key];
  const url = deal?.source_url || deal?.contract_link;
  try {
    if (url) return new URL(String(url).trim()).hostname.replace(/^www\./, '');
  } catch {
    /* fall through */
  }
  return null;
}

/** The one link worth offering as "Open source notice", or null. */
export function specificNoticeLink(deal) {
  for (const raw of [deal?.contract_link, deal?.source_url]) {
    if (isSpecificNoticeUrl(raw)) return String(raw).trim();
  }
  return null;
}

const STOP = new Set(['the', 'and', 'inc', 'ltd', 'llc', 'gmbh', 'group', 'limited', 'company', 'services', 'information', 'solutions', 'software', 'sa', 'sl', 'srl', 'spa', 'bv']);

function nameTokens(...values) {
  const out = new Set();
  for (const v of values) {
    for (const w of String(v ?? '').toLowerCase().split(/[^a-z0-9à-ÿ]+/)) {
      if (w.length >= 4 && !STOP.has(w)) out.add(w);
    }
  }
  return out;
}

/**
 * Is the sentiment evidence about THIS deal's vendor? On Universidade do Porto × Ex Libris the
 * stored rationale was about Turnitin and Moodle. A rating explained by another vendor's evidence
 * must not be shown as this incumbent's position. No rationale → trust the rating alone.
 */
export function sentimentEvidenceMatchesVendor(deal) {
  const text = String(deal?.incumbent_sentiment_rationale ?? '').toLowerCase().trim();
  if (!text) return true;
  const tokens = nameTokens(deal?.supplier, deal?.incumbent_vendor, deal?.product_name);
  if (!tokens.size) return true;
  for (const t of tokens) if (text.includes(t)) return true;
  return false;
}

export function sentimentLevel(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s.startsWith('pos')) return 'positive';
  if (s.startsWith('neg')) return 'negative';
  if (s.startsWith('neu')) return 'neutral';
  return null;
}

/** "How we dated this" — from the term and the grade, never from the audit note. */
export function expiryBasisText(deal) {
  const term = Number(deal?.initial_term ?? deal?.initial_term_months);
  const grade = String(deal?.predicted_expiry_confidence ?? '').trim().toUpperCase();
  if (Number.isFinite(term) && term > 0) {
    return `Expiry = award date + the ${term}-month term stated in the award`;
  }
  if (grade === 'H') return 'Expiry from the published end date';
  if (grade === 'M') return 'Expiry from the published contract length; some details inferred';
  if (grade === 'L') return 'Approximate: estimated from typical contract cycles';
  return null;
}

/**
 * H / M / L from the row, or 'T' when there is no grade but the expiry rests on a published
 * term (Exeter × YouGov: 15 months from Aug 2025, grade null) — solid enough to show.
 */
export function expiryGrade(deal) {
  const g = String(deal?.predicted_expiry_confidence ?? '').trim().toUpperCase();
  if (g === 'H' || g === 'M' || g === 'L') return g;
  const term = Number(deal?.initial_term ?? deal?.initial_term_months);
  return Number.isFinite(term) && term > 0 ? 'T' : null;
}

// 'T' (a published term, no grade) is labelled High: the date comes from the contract itself,
// which is what the badge tooltip calls High. One vocabulary for the client.
const GRADE_LABEL = { H: 'High', M: 'Medium', L: 'Low', T: 'High' };
export function expiryGradeLabel(grade) {
  return GRADE_LABEL[grade] || null;
}

function validDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Position of today on the award → expiry bar, 0–100, or null when either end is missing. */
export function timelineProgress(deal, now = new Date()) {
  const start = validDate(deal?.first_award_date ?? deal?.published_date);
  const end = validDate(deal?.predicted_expiry ?? deal?.predicted_expiry_date);
  if (!start || !end || end <= start) return null;
  const pct = ((now - start) / (end - start)) * 100;
  return Math.max(0, Math.min(100, pct));
}

/** Where the 12-month decision window starts on the same bar, 0–100. */
export function decisionWindowStart(deal) {
  const start = validDate(deal?.first_award_date ?? deal?.published_date);
  const end = validDate(deal?.predicted_expiry ?? deal?.predicted_expiry_date);
  if (!start || !end || end <= start) return null;
  const open = new Date(end);
  open.setFullYear(open.getFullYear() - 1);
  const pct = ((open - start) / (end - start)) * 100;
  return Math.max(0, Math.min(100, pct));
}

function supplierFamily(name) {
  const s = String(name ?? '').trim();
  if (!s) return null;
  // "EBSCO Information Services S.L.U." and "EBSCO INFORMATION SERVICES S.R.L." are one vendor.
  const first = s.split(/[\s,(]+/)[0];
  return first.length >= 3 ? first.toUpperCase() : s.toUpperCase();
}

// Same category, same country, and the same kind of buyer when both rows say: a university's
// library system is not priced like a town library's journal subscription.
function sameScope(a, b) {
  if (!a || !b || a.category !== b.category || !a.country || a.country !== b.country) return false;
  if (a.institution_type && b.institution_type && a.institution_type !== b.institution_type) return false;
  return true;
}

function productKey(deal) {
  const first = String(deal?.product_name ?? '').trim().split(/[\s+,(/]+/).find((w) => w.length >= 3);
  return first ? first.toLowerCase() : null;
}

/**
 * Who supplies this category in this country: distinct institutions per vendor, from the closed
 * deals already loaded in Contract Intelligence. Needs 5+ institutions to say anything.
 */
export function marketShareRows(deal, peers, { limit = 5 } = {}) {
  const byVendor = new Map();
  const labels = new Map();
  const institutions = new Set();
  for (const p of peers || []) {
    if (!sameScope(p, deal)) continue;
    const fam = supplierFamily(p.supplier);
    const inst = String(p.account_name ?? p.institution ?? '').trim();
    if (!fam || !inst) continue;
    institutions.add(inst);
    if (!byVendor.has(fam)) byVendor.set(fam, new Set());
    byVendor.get(fam).add(inst);
    if (!labels.has(fam)) labels.set(fam, String(p.supplier).trim());
  }
  if (institutions.size < 5) return null;
  const rows = [...byVendor.entries()]
    .map(([fam, set]) => ({ key: fam, label: labels.get(fam), institutions: set.size }))
    .sort((a, b) => b.institutions - a.institutions)
    .slice(0, limit);
  const max = rows[0]?.institutions || 1;
  return { rows: rows.map((r) => ({ ...r, pct: (100 * r.institutions) / max })), total: institutions.size };
}

/** Priced deals from the same supplier or product in this country, most recent first. */
export function pricePeerRows(deal, peers, { limit = 5 } = {}) {
  const selfId = String(deal?.deal_id ?? '');
  const priced = (peers || []).filter((p) => sameScope(p, deal) && Number(p.deal_value_usd) > 0 && String(p.deal_id ?? '') !== selfId);
  const byDate = (a, b) => String(b.first_award_date ?? '').localeCompare(String(a.first_award_date ?? ''));
  const fam = supplierFamily(deal?.supplier);
  const prod = productKey(deal);
  const same = priced.filter((p) => (fam && supplierFamily(p.supplier) === fam) || (prod && productKey(p) === prod));
  // A category-wide fallback was tried and dropped: in Chilean LMS it listed a microscope and a
  // server next to Blackboard. No comparison beats a misleading one.
  if (same.length >= 2) return { basis: 'same', rows: same.sort(byDate).slice(0, limit) };
  return null;
}

/** "€406,833" in the deal's own currency; null when there is no native amount. */
export function formatNativeValue(deal) {
  const n = Number(deal?.deal_value);
  const cur = String(deal?.currency ?? '').trim().toUpperCase();
  if (!Number.isFinite(n) || n <= 0 || !/^[A-Z]{3}$/.test(cur)) return null;
  if (cur === 'USD') return `$${Math.round(n).toLocaleString('en-US')}`; // en-GB prints "US$"
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${cur} ${Math.round(n).toLocaleString()}`;
  }
}

export function formatUsdShort(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n)}`;
}

/** Register titles can be a whole purchase order; keep the first ~200 characters, on a word. */
export function clipTitle(text, max = 200) {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > max * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s,.;:–-]+$/, '')}…`;
}

/**
 * Stack product labels sometimes carry contract prose ("This SPC requires that Watermark
 * Insights, LLC provide Software", East Stroudsburg). A tile shows names, not sentences.
 */
export function isDisplayableStackLabel(label) {
  const s = String(label ?? '').trim();
  if (!s || s.length > 40) return false;
  return s.split(/\s+/).length <= 5;
}

export function monthsAwayText(months) {
  if (months == null) return null;
  if (months <= 0) return 'This month';
  return months === 1 ? '1 month away' : `${months} months away`;
}
